import { ActionPayloadValidationError, requireStringField } from "@slot/client-engine";
import type { ReplayContext, RoundAction, RoundActionHandler } from "@slot/client-engine";

/** The Secret Bonus on the board: the cell its symbol landed on and the purchase offer a tap on it opens. */
export interface SecretBonusOffer {
  positionIndex: number;
  offerIdentifier: string;
}

/** What the handler drives (the feature's `SecretBonusPresenter`). */
export interface SecretBonusTarget {
  presentSecretBonus(offer: SecretBonusOffer): Promise<void>;
}

/**
 * GAME-DEFINED action `present_secret_bonus { positionIndex, offerIdentifier }` (decision 51): the Secret Bonus symbol
 * stands on the base spin's last board at `positionIndex` (the board the event carries says so), and a tap on it opens
 * the purchase offer `offerIdentifier` — an unlisted offer, so that tap is its only way in — until the next spin.
 *
 * SERVER-AUTHORITATIVE: the server decides whether and where the symbol lands, and which offer it opens; the client
 * shows the hand and routes the tap, and the purchase itself is the shell's (its confirm, its price, its checks). The
 * backend and the fixtures author it on a base spin's last event, before `spin_boundary_end`.
 */
export class PresentSecretBonusActionHandler implements RoundActionHandler {
  public readonly actionTypeName = "present_secret_bonus";

  public constructor(private readonly target: SecretBonusTarget) {}

  public async handleAction(roundAction: RoundAction, _context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;
    const positionIndex = requireStringField(this.actionTypeName, payload, "positionIndex");
    if (!/^\d+$/.test(positionIndex)) throw new ActionPayloadValidationError(this.actionTypeName, "positionIndex", `Must be a cell index, got ${JSON.stringify(positionIndex)}.`);
    const offerIdentifier = requireStringField(this.actionTypeName, payload, "offerIdentifier");
    if (offerIdentifier.length === 0) throw new ActionPayloadValidationError(this.actionTypeName, "offerIdentifier", "Must name an offer.");
    await this.target.presentSecretBonus({ positionIndex: Number(positionIndex), offerIdentifier });
  }
}
